import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreateDette,
  useUpdateDette,
  type DetteFournisseurRow,
  type DetteFournisseurStatut,
} from '@/hooks/queries/useDettesFournisseurs';
import { useTiers, useUpsertTier } from '@/hooks/queries/useTiers';
import { useFormations } from '@/hooks/queries/useFormation';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  activiteId: string | null;
  dette?: DetteFournisseurRow | null;
}

function formatNumberInput(n: number | ''): string {
  if (n === '' || n === 0) return '';
  return n.toLocaleString('fr-DZ', { maximumFractionDigits: 0 });
}

function parseNumberInput(s: string): number {
  const cleaned = s.replace(/[^\d-]/g, '');
  if (!cleaned || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

function computeStatut(dette: number, regle: number): DetteFournisseurStatut {
  if (regle <= 0) return 'a_payer';
  if (regle >= dette) return 'paye';
  return 'partiel';
}

const STATUT_LABEL: Record<DetteFournisseurStatut, string> = {
  a_payer: 'À payer',
  partiel: 'Partiel',
  paye: 'Payé',
};

export function CreateDetteFournisseurDialog({
  open,
  onOpenChange,
  entrepriseId,
  activiteId,
  dette,
}: Props): JSX.Element {
  const isEdit = !!dette;
  const create = useCreateDette();
  const update = useUpdateDette();
  const upsertTier = useUpsertTier();
  const { data: tiers = [] } = useTiers({ entrepriseId, type: 'fournisseur', search: '' });
  const { data: formations = [] } = useFormations({ entrepriseId });

  const [annee, setAnnee] = useState<number>(new Date().getFullYear());
  const [fournisseurNom, setFournisseurNom] = useState('');
  const [fournisseurTierId, setFournisseurTierId] = useState<string>('');
  const [telephone, setTelephone] = useState('');
  const [rib, setRib] = useState('');
  const [email, setEmail] = useState('');
  const [formationTitre, setFormationTitre] = useState('');
  const [formationId, setFormationId] = useState<string>('');
  const [dateFormation, setDateFormation] = useState('');
  const [referenceFacture, setReferenceFacture] = useState('');
  const [montantDette, setMontantDette] = useState<number | ''>('');
  const [montantDetteDisplay, setMontantDetteDisplay] = useState('');
  const [montantRegle, setMontantRegle] = useState<number | ''>('');
  const [montantRegleDisplay, setMontantRegleDisplay] = useState('');
  const [observation, setObservation] = useState('');

  useEffect(() => {
    if (!open) return;
    if (dette) {
      setAnnee(dette.annee);
      setFournisseurNom(dette.fournisseur_nom);
      setFournisseurTierId(dette.fournisseur_tier_id ?? '');
      setTelephone(dette.fournisseur_telephone ?? '');
      setRib(dette.fournisseur_rib ?? '');
      setEmail(dette.fournisseur_email ?? '');
      setFormationTitre(dette.formation_titre ?? '');
      setFormationId(dette.formation_id ?? '');
      setDateFormation(dette.date_formation ?? '');
      setReferenceFacture(dette.reference_facture ?? '');
      setMontantDette(dette.montant_dette);
      setMontantDetteDisplay(formatNumberInput(dette.montant_dette));
      setMontantRegle(dette.montant_regle);
      setMontantRegleDisplay(formatNumberInput(dette.montant_regle));
      setObservation(dette.observation ?? '');
    } else {
      setAnnee(new Date().getFullYear());
      setFournisseurNom('');
      setFournisseurTierId('');
      setTelephone('');
      setRib('');
      setEmail('');
      setFormationTitre('');
      setFormationId('');
      setDateFormation('');
      setReferenceFacture('');
      setMontantDette('');
      setMontantDetteDisplay('');
      setMontantRegle('');
      setMontantRegleDisplay('');
      setObservation('');
    }
  }, [open, dette]);

  // Auto-resolve tier + auto-fill coordonnées si match
  useEffect(() => {
    const match = tiers.find(
      (t) => t.raison_sociale.trim().toLowerCase() === fournisseurNom.trim().toLowerCase(),
    );
    if (match) {
      setFournisseurTierId(match.id);
      if (!telephone && match.telephone) setTelephone(match.telephone);
      if (!rib && match.rib) setRib(match.rib);
      if (!email && match.email) setEmail(match.email);
    } else if (fournisseurTierId && match === undefined) {
      setFournisseurTierId('');
    }
  }, [fournisseurNom, tiers, telephone, rib, email, fournisseurTierId]);

  useEffect(() => {
    const match = formations.find(
      (f) => f.titre.trim().toLowerCase() === formationTitre.trim().toLowerCase(),
    );
    if (match) setFormationId(match.id);
    else if (formationId) setFormationId('');
  }, [formationTitre, formations, formationId]);

  const fournisseursList = useMemo(
    () => tiers.slice().sort((a, b) => a.raison_sociale.localeCompare(b.raison_sociale)),
    [tiers],
  );
  const formationsList = useMemo(
    () => formations.slice().sort((a, b) => a.titre.localeCompare(b.titre)),
    [formations],
  );

  const dette_n = typeof montantDette === 'number' ? montantDette : 0;
  const regle_n = typeof montantRegle === 'number' ? montantRegle : 0;
  const statutCalculated = computeStatut(dette_n, regle_n);
  const reste = Math.max(0, dette_n - regle_n);

  const submit = async (): Promise<void> => {
    if (!fournisseurNom.trim()) {
      toast.error('Nom du fournisseur obligatoire');
      return;
    }
    if (!entrepriseId) {
      toast.error('Entreprise non sélectionnée');
      return;
    }
    if (dette_n <= 0) {
      toast.error('Montant dette > 0 obligatoire');
      return;
    }
    if (regle_n > dette_n) {
      toast.error('Montant réglé ne peut excéder le montant dû');
      return;
    }

    try {
      let resolvedTierId = fournisseurTierId;
      if (!resolvedTierId && fournisseurNom.trim()) {
        const created = await upsertTier.mutateAsync({
          entreprise_id: entrepriseId,
          type: 'fournisseur',
          raison_sociale: fournisseurNom.trim(),
          telephone: telephone.trim() || null,
          email: email.trim() || null,
          activite_principale_id: activiteId,
        } as never);
        resolvedTierId = created.id;
        toast.success(`Nouveau fournisseur créé : ${fournisseurNom.trim()}`);
      }

      let resolvedFormationId = formationId;
      if (!resolvedFormationId && formationTitre.trim()) {
        const code = `IMP-${Math.floor(Math.random() * 9000 + 1000)}`;
        const { data: createdF, error: errF } = await supabase
          .from('formations')
          .insert({
            entreprise_id: entrepriseId,
            code,
            titre: formationTitre.trim(),
          } as never)
          .select('id')
          .single();
        if (errF) {
          // eslint-disable-next-line no-console
          console.warn('Formation creation skipped:', errF.message);
        } else {
          resolvedFormationId = (createdF as { id: string } | null)?.id ?? '';
        }
      }

      if (isEdit && dette) {
        await update.mutateAsync({
          id: dette.id,
          annee,
          fournisseur_tier_id: resolvedTierId || null,
          fournisseur_nom: fournisseurNom.trim(),
          fournisseur_telephone: telephone.trim() || null,
          fournisseur_rib: rib.trim() || null,
          fournisseur_email: email.trim() || null,
          formation_id: resolvedFormationId || null,
          formation_titre: formationTitre.trim() || null,
          date_formation: dateFormation || null,
          reference_facture: referenceFacture.trim() || null,
          montant_dette: dette_n,
          montant_regle: regle_n,
          statut: statutCalculated,
          observation: observation.trim() || null,
        });
        toast.success('Dette modifiée');
      } else {
        await create.mutateAsync({
          entreprise_id: entrepriseId,
          activite_id: activiteId,
          fournisseur_tier_id: resolvedTierId || null,
          fournisseur_nom: fournisseurNom.trim(),
          fournisseur_telephone: telephone.trim() || null,
          fournisseur_rib: rib.trim() || null,
          fournisseur_email: email.trim() || null,
          annee,
          formation_id: resolvedFormationId || null,
          formation_titre: formationTitre.trim() || null,
          date_formation: dateFormation || null,
          reference_facture: referenceFacture.trim() || null,
          montant_dette: dette_n,
          montant_regle: regle_n,
          statut: statutCalculated,
          mode_paiement: null,
          reference_operation: null,
          date_virement: null,
          observation: observation.trim() || null,
        });
        toast.success(
          regle_n > 0 && regle_n < dette_n
            ? `Dette créée · statut partiel (reste ${reste.toLocaleString('fr-DZ')} DA)`
            : 'Dette créée',
        );
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  const isSubmitting = create.isPending || update.isPending || upsertTier.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col p-0">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier la dette' : 'Nouvelle dette fournisseur'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="Fournisseur"
            required
            hint={
              fournisseurTierId
                ? '✓ Fournisseur existant lié — coordonnées auto-remplies'
                : fournisseurNom.trim()
                  ? '⚠ Nouveau fournisseur — sera créé automatiquement à la sauvegarde'
                  : 'Tapez ou choisissez dans la liste'
            }
            warn={!fournisseurTierId && !!fournisseurNom.trim()}
          >
            <input
              list="fournisseurs-list"
              value={fournisseurNom}
              onChange={(e) => setFournisseurNom(e.target.value)}
              className="input"
              placeholder="Nom du fournisseur"
              required
            />
            <datalist id="fournisseurs-list">
              {fournisseursList.map((t) => (
                <option key={t.id} value={t.raison_sociale} />
              ))}
            </datalist>
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Téléphone">
              <input
                type="tel"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                className="input"
                placeholder="0555 XX XX XX"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="contact@…"
              />
            </Field>
            <Field label="N° compte (RIB)">
              <input
                value={rib}
                onChange={(e) => setRib(e.target.value)}
                className="input font-mono text-xs"
                placeholder="CPA — DZ59…"
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Année" required>
              <input
                type="number"
                value={annee}
                onChange={(e) => setAnnee(Number(e.target.value))}
                className="input"
                min={2000}
                max={2100}
                required
              />
            </Field>
            <Field label="Date facture">
              <input
                type="date"
                value={dateFormation}
                onChange={(e) => setDateFormation(e.target.value)}
                className="input"
              />
            </Field>
            <Field label="N° facture">
              <input
                value={referenceFacture}
                onChange={(e) => setReferenceFacture(e.target.value)}
                className="input"
                placeholder="FAC-2024-…"
              />
            </Field>
          </div>

          <Field
            label="Formation liée (optionnel)"
            hint={
              formationId
                ? '✓ Formation existante liée'
                : formationTitre.trim()
                  ? '⚠ Nouvelle formation — sera créée automatiquement'
                  : 'Vide si dette non liée à une formation'
            }
            warn={!formationId && !!formationTitre.trim()}
          >
            <input
              list="formations-list"
              value={formationTitre}
              onChange={(e) => setFormationTitre(e.target.value)}
              className="input"
              placeholder="Titre de la formation"
            />
            <datalist id="formations-list">
              {formationsList.map((f) => (
                <option key={f.id} value={f.titre} />
              ))}
            </datalist>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Montant dette (DA)" required>
              <input
                value={montantDetteDisplay}
                onChange={(e) => {
                  const n = parseNumberInput(e.target.value);
                  setMontantDette(n === 0 ? '' : n);
                  setMontantDetteDisplay(e.target.value.replace(/[^\d ,.-]/g, ''));
                }}
                onBlur={() =>
                  montantDette !== '' && setMontantDetteDisplay(formatNumberInput(montantDette))
                }
                className="input tabular-nums"
                placeholder="250 000"
                inputMode="numeric"
                required
              />
            </Field>
            <Field
              label="Montant déjà réglé (DA)"
              hint={
                regle_n === 0
                  ? 'Statut : À payer'
                  : regle_n >= dette_n
                    ? '✓ Statut : Payé (intégralement)'
                    : `⚠ Statut : Partiel — reste ${reste.toLocaleString('fr-DZ')} DA`
              }
              warn={regle_n > 0 && regle_n < dette_n}
            >
              <input
                value={montantRegleDisplay}
                onChange={(e) => {
                  const n = parseNumberInput(e.target.value);
                  setMontantRegle(n === 0 ? '' : n);
                  setMontantRegleDisplay(e.target.value.replace(/[^\d ,.-]/g, ''));
                }}
                onBlur={() =>
                  montantRegle !== '' && setMontantRegleDisplay(formatNumberInput(montantRegle))
                }
                className="input tabular-nums"
                placeholder="0"
                inputMode="numeric"
              />
            </Field>
          </div>

          <Field label="Observation">
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              className="input min-h-[60px]"
              rows={2}
              placeholder="Reste à payer, conditions, …"
            />
          </Field>

          <div
            className={cn(
              'rounded-lg border px-3 py-2 text-xs',
              statutCalculated === 'paye'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : statutCalculated === 'partiel'
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-slate-200 bg-slate-50 text-slate-700',
            )}
          >
            <strong>Statut calculé :</strong> {STATUT_LABEL[statutCalculated]}
            {!isEdit && statutCalculated !== 'a_payer' && (
              <span className="block text-[11px] opacity-80">
                Note : la création n&apos;émet PAS d&apos;opération Caisse. Utilisez{' '}
                <em>Enregistrer paiement</em> dans le drawer pour générer une opération SORTIE.
              </span>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isSubmitting}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {isEdit ? 'Enregistrer' : 'Créer la dette'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  required,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
